export type ChessClockSide = 'white' | 'black';

/**
 * Someone seated at a half of the clock. The side is the colour they drew,
 * which is not which half they sit on: the person holding the phone is the
 * near one whichever colour they have.
 */
export interface ChessClockPlayer {
  /** How the name is written, @ and all. A passerby has none. */
  name: string;
  side: ChessClockSide;
}

export type ChessClockPhase = 'idle' | 'running' | 'paused' | 'flagged';

/** The clock this phone is keeping. Idle removes it, so a missing one is idle. */
export const CHESS_CLOCK_STORAGE_KEY = 'gambits.chess-clock';

/** Where the clock on this phone is, including after leaving the clock screen. */
export function readChessClockPhase(storage: Pick<Storage, 'getItem'> = sessionStorage): ChessClockPhase {
  try {
    const raw = storage.getItem(CHESS_CLOCK_STORAGE_KEY);
    if (!raw) return 'idle';

    const phase = (JSON.parse(raw) as { phase?: unknown }).phase;
    if (phase === 'running' || phase === 'paused' || phase === 'flagged') return phase;

    return 'idle';
  } catch {
    return 'idle';
  }
}

export interface ChessClockControl {
  /** The bank each side starts with. */
  minutes: number;
  /** Fischer increment, in seconds, given to a side when it ends a move. */
  increment: number;
}

export interface ChessClockState {
  control: ChessClockControl;
  phase: ChessClockPhase;
  /** The side to move. Null before the first press and once someone has flagged. */
  turn: ChessClockSide | null;
  /**
   * What each side had left when its clock was last stopped. The running side's
   * real remaining time is this less the time since `activeSinceMs`, which is
   * why nothing here is decremented on a timer: a clock that subtracts ticks
   * loses whatever the browser throttles away while the tab is in the
   * background or the phone is asleep.
   */
  bankedMs: Record<ChessClockSide, number>;
  /** When the running side's clock was started. Null unless running. */
  activeSinceMs: number | null;
  flagged: ChessClockSide | null;
}

const MS_PER_SECOND = 1000;
const MS_PER_MINUTE = 60 * MS_PER_SECOND;

export const MIN_CLOCK_MINUTES = 1;
export const MAX_CLOCK_MINUTES = 180;
export const MAX_CLOCK_INCREMENT = 60;

export const DEFAULT_CHESS_CLOCK_CONTROL: ChessClockControl = { minutes: 10, increment: 3 };

export const CHESS_CLOCK_PRESETS: ChessClockControl[] = [
  { minutes: 10, increment: 0 },
  { minutes: 10, increment: 3 },
  { minutes: 5, increment: 3 },
];

/**
 * Below this the numerals carry tenths. It is a constant rather than a setting
 * so the figures never change shape in the middle of a move.
 */
export const TENTHS_BELOW_MS = 20 * MS_PER_SECOND;

const SIDES: ChessClockSide[] = ['white', 'black'];

export function otherSide(side: ChessClockSide): ChessClockSide {
  return side === 'white' ? 'black' : 'white';
}

export function createChessClock(control: ChessClockControl): ChessClockState {
  const bank = Math.max(0, Math.round(control.minutes * MS_PER_MINUTE));

  return {
    control: { ...control },
    phase: 'idle',
    turn: null,
    bankedMs: { white: bank, black: bank },
    activeSinceMs: null,
    flagged: null,
  };
}

export function readRemainingMs(state: ChessClockState, side: ChessClockSide, nowMs: number): number {
  const banked = state.bankedMs[side];

  if (state.phase !== 'running' || state.turn !== side || state.activeSinceMs === null) {
    return banked;
  }

  return Math.max(0, banked - (nowMs - state.activeSinceMs));
}

function flag(state: ChessClockState, side: ChessClockSide): ChessClockState {
  return {
    ...state,
    phase: 'flagged',
    turn: null,
    bankedMs: { ...state.bankedMs, [side]: 0 },
    activeSinceMs: null,
    flagged: side,
  };
}

/**
 * A press hands the move over, the way pressing a lever on a real clock does:
 * the pressed side stops and takes its increment, and the other side starts.
 */
export function press(state: ChessClockState, side: ChessClockSide, nowMs: number): ChessClockState {
  if (state.phase === 'flagged') return state;

  // A press on a paused clock sets it going again rather than playing a move:
  // nothing happened on the board while it stood still, so whose move it is
  // does not change and no increment is due. Either half will do it, so the
  // game restarts from wherever the players' hands already are.
  if (state.phase === 'paused') return resume(state, nowMs);

  // The opening press is the same gesture with nothing behind it: whoever is
  // not moving first presses their own half to set the other side going. No
  // move has been played, so it earns no increment.
  if (state.phase === 'idle') {
    return { ...state, phase: 'running', turn: otherSide(side), activeSinceMs: nowMs };
  }

  // The half that is not on the move is inert. Without this either player can
  // press twice in a row and hand themselves the other's time.
  if (state.turn !== side) return state;

  const remaining = readRemainingMs(state, side, nowMs);
  if (remaining <= 0) return flag(state, side);

  return {
    ...state,
    turn: otherSide(side),
    bankedMs: { ...state.bankedMs, [side]: remaining + state.control.increment * MS_PER_SECOND },
    activeSinceMs: nowMs,
  };
}

/** A pause is not a move: it banks what is left and awards nothing. */
export function pause(state: ChessClockState, nowMs: number): ChessClockState {
  if (state.phase !== 'running' || state.turn === null) return state;

  return {
    ...state,
    phase: 'paused',
    bankedMs: { ...state.bankedMs, [state.turn]: readRemainingMs(state, state.turn, nowMs) },
    activeSinceMs: null,
  };
}

export function resume(state: ChessClockState, nowMs: number): ChessClockState {
  if (state.phase !== 'paused') return state;

  return { ...state, phase: 'running', activeSinceMs: nowMs };
}

export function reset(state: ChessClockState): ChessClockState {
  return createChessClock(state.control);
}

/**
 * Called on every repaint. It only ever moves a running clock to flagged, so a
 * caller can assign the result unconditionally without churning the state.
 */
export function tick(state: ChessClockState, nowMs: number): ChessClockState {
  if (state.phase !== 'running' || state.turn === null) return state;
  if (readRemainingMs(state, state.turn, nowMs) > 0) return state;

  return flag(state, state.turn);
}

/** `m:ss` at a glance, tenths once the game is close enough that they matter. */
export function formatRemaining(ms: number): string {
  const clamped = Math.max(0, ms);

  if (clamped < TENTHS_BELOW_MS) {
    const seconds = Math.floor(clamped / MS_PER_SECOND);
    const tenths = Math.floor((clamped % MS_PER_SECOND) / 100);

    return `${seconds}.${tenths}`;
  }

  const totalSeconds = Math.floor(clamped / MS_PER_SECOND);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;

  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

export function formatControl(control: ChessClockControl): string {
  return `${control.minutes}+${control.increment}`;
}

function clampWhole(value: unknown, min: number, max: number, fallback: number): number {
  if (value === null || value === undefined || value === '') return fallback;

  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  return Math.min(max, Math.max(min, Math.round(parsed)));
}

/**
 * Time controls arrive as query parameters, so anything can be in them. Junk
 * falls back to the default rather than producing a clock that cannot run.
 */
export function normalizeControl(minutes: unknown, increment: unknown): ChessClockControl {
  return {
    minutes: clampWhole(minutes, MIN_CLOCK_MINUTES, MAX_CLOCK_MINUTES, DEFAULT_CHESS_CLOCK_CONTROL.minutes),
    increment: clampWhole(increment, 0, MAX_CLOCK_INCREMENT, DEFAULT_CHESS_CLOCK_CONTROL.increment),
  };
}

export function isSameControl(left: ChessClockControl, right: ChessClockControl): boolean {
  return left.minutes === right.minutes && left.increment === right.increment;
}

function isSide(value: unknown): value is ChessClockSide {
  return value === 'white' || value === 'black';
}

/**
 * Rebuilds a clock from its stored snapshot. A snapshot taken under a different
 * time control is discarded rather than adapted, so arriving at the clock with
 * new settings starts a new game instead of resuming an old one.
 */
export function restoreChessClock(raw: unknown, control: ChessClockControl): ChessClockState | null {
  if (!raw || typeof raw !== 'object') return null;

  const state = raw as Partial<ChessClockState>;

  if (!state.control || typeof state.control !== 'object') return null;
  if (!isSameControl(state.control as ChessClockControl, control)) return null;

  const phase = state.phase;
  if (phase !== 'idle' && phase !== 'running' && phase !== 'paused' && phase !== 'flagged') return null;

  const banked = state.bankedMs;
  if (!banked || SIDES.some((side) => !Number.isFinite(banked[side]))) return null;

  if (state.turn !== null && !isSide(state.turn)) return null;
  if (state.flagged !== null && !isSide(state.flagged)) return null;
  if (state.activeSinceMs !== null && !Number.isFinite(state.activeSinceMs)) return null;

  // A running clock with no start time, or a stopped one that still has one,
  // cannot be read: there is no way to tell how much of the bank is already
  // spent, so the game is better restarted than silently mistimed.
  if ((phase === 'running') !== (state.activeSinceMs !== null && state.activeSinceMs !== undefined)) return null;
  if (phase === 'running' && !isSide(state.turn)) return null;

  return {
    control: { ...control },
    phase,
    turn: isSide(state.turn) ? state.turn : null,
    bankedMs: { white: Math.max(0, banked.white), black: Math.max(0, banked.black) },
    activeSinceMs: state.activeSinceMs ?? null,
    flagged: isSide(state.flagged) ? state.flagged : null,
  };
}
